import { SubButton } from '@/common/buttons/SubButton';
import { useDialog } from '@/common/dialog/DialogContext';
import { useInDialog } from '@/common/dialog/DialogScopeContext';
import { useEntityChange } from '@/common/entityChange/EntityChangeContext';
import { ConfirmDeleteDialog } from '@/common/entityFramework/entityList/ConfirmDeleteDialog';
import { DateUtils } from '@/common/utils/DateUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box } from '@mui/material';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { AddButton } from '../../buttons/AddButton';
import { DEFAULT_GRID_OPTIONS, Grid } from '../../grid/Grid';
import { useEntityFromRoute } from '../../hooks/route/useEntityFromRoute';
import { useModuleFromRoute } from '../../hooks/route/useModuleFromRoute';
import { useNavState } from '../../navState/NavStateContext';
import { usePrincipal } from '../../principal/PrincipalContext';
import { useRsrc } from '../../rsrc/RsrcContext';
import { EntityUtils } from '../../utils/EntityUtils';
import { ExprUtils } from '../../utils/ExprUtils';
import { StringUtils } from '../../utils/StringUtils';
import { EntityBanner } from '../entityBanner/EntityBanner';
import { useTab } from '../entityDetail/TabContext';
import { EntityTitle } from '../entityTitle/EntityTitle';
import { SearchBox } from '../entityTitle/SearchBox';
import { useEntityMgr } from '../entityView/EntityMgrContext';
import { Mode } from '../enumerations/Mode';


export const EntityList = ({
  id,

  // Optional Module and Entity. If not provided they will be grabbed from the route
  module,
  entity,

  // Optional Modifications to EntityList 
  augFieldDefs,
  augRowsReq,
  clientSideRowData,

  // Optional Modifications to the Grid
  gridOptions = {},
  events = {},

  // Optional augmentation of buttons in main lists
  augButtons,
  // Optional override of buttons for lists in a Tab
  augTabButtons,

  // Optional lever for showing the title and banner
  showTitle = true,

  // Ref
  ref
}) => {

  const { tabLabel } = useTab();
  const inDialog = useInDialog();
  if (tabLabel && tabLabel !== 'Info' && !entity) {
    throw new Error(`An Entity needs to be provided to the EntityList in tab ${tabLabel}. This is a development error`);
  }

  // Module and Entity
  const routeModule = useModuleFromRoute();
  module ??= routeModule;
  const routeEntity = useEntityFromRoute();
  entity ??= routeEntity;

  // Context
  const rsrc = useRsrc();
  const { hist, setExportReq, entityDef } = useEntityMgr();
  const { getFieldRoleSet, hasRole } = usePrincipal();
  const { navigateTo, navState } = useNavState();
  const { openDialog } = useDialog();

  // Ref
  const gridRef = useRef(null);

  // State
  const [fieldDefs, setFieldDefs] = useState();

  // API to Parent
  useImperativeHandle(ref, () => {
    return {
      fieldDefs,
      getContextMenuItems,
      gridRef: gridRef,
    }
  }, [fieldDefs])

  // First must fetch fieldDefs
  useEffect(() => {
    rsrc.fieldDefs(module, entity).then(fieldDefs => {

      // If needed, augment the fieldDefs
      if (augFieldDefs) {
        const fieldToFieldDef = Object.fromEntries(fieldDefs.map(fieldDef => [fieldDef.field, fieldDef]));
        augFieldDefs(fieldToFieldDef);
        fieldDefs = Object.values(fieldToFieldDef);
      }

      // Filter fieldDefs by permissions
      fieldDefs = EntityUtils.filterAuthorizedFields(module, entity, fieldDefs, getFieldRoleSet);

      // If this grid is showing the hist of an Entity, remove checkbox column
      if (hist) {
        fieldDefs = fieldDefs.filter(fieldDef => !fieldDef.checkboxSelection)
      }

      fieldDefs.sort((a, b) => {
        // 1) checkbox field leads
        if (a.field === '' && b.field !== '') return -1;
        if (b.field === '' && a.field !== '') return 1;

        // 2) draggable field next
        if (a.rowDrag && !b.rowDrag) return -1;
        if (b.rowDrag && !a.rowDrag) return 1;

        // 3) then code
        if (a.field === 'code' && b.field !== 'code') return -1;
        if (b.field === 'code' && a.field !== 'code') return 1;

        // 4) finally, by numeric precedence (defaulting to 0)
        return (a.precedence || 0) - (b.precedence || 0);
      });

      setFieldDefs(fieldDefs);
    })
  }, [])


  const { refreshTrigger, subscrToEntityChange, unsubscrToEntityChange } = useEntityChange();
  useEffect(() => {
    subscrToEntityChange(module, entity);
    return () => {
      unsubscrToEntityChange(module, entity);
    };
  }, []);

  useEffect(() => {
    if (!gridRef.current) return;
    refreshGrid();
  }, [refreshTrigger, augRowsReq, clientSideRowData]);


  const onGridReady = (params) => {
    if (params.api.getGridOption('rowModelType') === 'clientSide') {
      clientSideRowData(dataSource => gridRef.current.setDataSource(dataSource));
    } else {
      serverSideDataSource();
    }
  };

  const serverSideDataSource = () => {
    gridRef.current.setCount(null);
    const dataSource = {
      getRows: (params) => {
        const grid = gridRef.current;
        if (!grid) return;

        const rowsReq = buildRowsReq(params);
        buildAndSetExportReq(rowsReq);

        rsrc.rows(module, entity, rowsReq).then(rows => {
          grid.onGridSuccess(rows, params);
          grid.resize();

          rsrc.rowCount(module, entity, rowsReq).then(count => {
            grid.setCount(count);
          })
        })
      }
    };

    gridRef.current.setDataSource(dataSource);
  }

  const buildRowsReq = (params) => {
    const rowsReq = {};

    rowsReq.startRow = params.startRow;
    rowsReq.endRow = params.endRow;

    const paramExpr = ExprUtils.filterModelToExpr(params.filterModel, fieldDefs);

    const exprs = [paramExpr];

    // // AsOfDate
    // if (this.asOfDate) {
    //   exprs.push(ExprUtils.fieldExpr('verStartInstant', 'IS_LESS', this.asOfDate))
    //   exprs.push(ExprUtils.fieldExpr('verEndInstant', 'IS_GREATER', this.asOfDate))
    // }

    // History
    if (hist) {
      rowsReq.hist = true;
      exprs.push(ExprUtils.fieldExpr('id', 'IS', navState.entityId))
    }

    if (navState.expr) {
      exprs.push(navState.expr);
    }

    if (navState.query) {
      rowsReq.query = navState.query;
    }

    if (navState.replaceParams) {
      rowsReq.replaceParams = navState.replaceParams;
    }

    // Here we combine all the expressions
    rowsReq.expr = ExprUtils.and(...exprs);

    rowsReq.sortModel = params.sortModel;

    rowsReq.partition = navState.partition;

    if (augRowsReq) {
      augRowsReq(rowsReq);
    }

    return rowsReq;
  }

  const buildAndSetExportReq = (rowsReq) => {
    const exportReq = {
      timeZoneName: DateUtils.timeZoneName(),
      displayName: entityDef.displayName,
      module,
      entity,
      label: tabLabel,
      rowsReq,
      fieldDefs,
      uiType: 'LIST'
    }

    setExportReq(exportReq);
  }

  const refreshGrid = () => {
    if (gridRef.current.getRowModelType() === 'serverSide') {
      gridRef.current.gridApiRef.current.refreshServerSide({ purge: true });
    } else if (gridRef.current.getRowModelType() === 'clientSide') {
      clientSideRowData(dataSource => gridRef.current.setDataSource(dataSource));
    } else {
      serverSideDataSource();
    }
  }

  const { entityObj } = useEntityMgr();
  const getContextMenuItems = (params) => {
  
    let navState = {
      mode: Mode.DETAIL, 
      entityId: params.node.data.id
    }
    if (entityObj?.partition) {
      navState.partition = entityObj.partition;
    }

    const contextMenuItems = [
      {
        name: 'Details',
        icon: fas.faInfoCircle,
        action: () => navigateTo(module + '/' + StringUtils.toKebabConvention(entity), navState)
      }
    ]

    if (hasRole(`${module.toUpperCase()}_${entity}_DELETE`)) {
      contextMenuItems.push({
        name: 'Delete',
        disabled: (params.node.data.status === 'APPROVED' || params.node.data.status === 'APPROVING'),
        icon: fas.faTrash,
        action: () => openConfirmDeleteDialog(params.node.data)
      })
    }

    return contextMenuItems;
  }

  const openConfirmDeleteDialog = (rowData) => {
    const data = {
      entity: entity,
      id: rowData.id,
      module: module,
    }
    openDialog(ConfirmDeleteDialog, { data })
  }


  /* Main buttons */
  const buttons = [];
  if (entityDef && entityDef.searchable) buttons.push(<SearchBox key="searchBox"/>);
  buttons.push(<SubButton key="subButton"/>, <AddButton key="addButton"/>);
  if (augButtons) augButtons(buttons);

  /* Intra-tab buttons */
  const defaultTabButtons = [<AddButton entity={entity} />];
  if (augTabButtons) augTabButtons(defaultTabButtons);

  /* If we are in a Tab or Dialog, we don't render the Title and Banner */
  showTitle = !tabLabel && !inDialog && showTitle;

  return (
    fieldDefs && (
      <>
        {showTitle &&
          <>
            <EntityTitle buttons={buttons} />
            <EntityBanner />
          </>
        }

        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', m: 0 }}>

          {/* Conditionally Render Buttons */}
          {tabLabel && tabLabel !== 'Info' && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1, gap: 2 }}>
              {defaultTabButtons}
            </Box>
          )}

          {/* Grid (Ensuring No Scroll) */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }} data-test-id="entity-list">
            <Grid
              id={id ? id + 'Grid' : undefined}
              ref={gridRef}
              gridOptions={{
                fieldDefs: fieldDefs,
                getContextMenuItems: getContextMenuItems,
                rowModelType: clientSideRowData ? 'clientSide' : DEFAULT_GRID_OPTIONS.rowModelType,
                ...gridOptions,
              }}
              events={{
                ...events,
                onGridReady
              }}
            />
          </Box>
        </Box>
      </>
    )
  );
}